import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function BulkImport() {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<any>(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreview(null);
            setResult(null);
            setError(null);
        }
    };

    const handlePreview = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setError(null);
            const response = await fetch('/api/v1/imports/preview', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
                }
            });

            const data = await response.json();
            if (data.success) {
                setPreview(data);
            } else {
                setError(data.message || 'Preview failed');
            }
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleImport = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setImporting(true);
            setError(null);

            const response = await fetch('/api/v1/imports/products', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
                }
            });

            const data = await response.json();
            if (data.success) {
                setResult(data);
                setFile(null);
                setPreview(null);
            } else {
                setError(data.message || 'Import failed');
                setResult(data);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = () => {
        window.location.href = '/api/v1/imports/template?format=csv';
    };

    return (
        <div className="mt-8 max-w-4xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-800">Bulk Product Import</h1>
                <p className="text-slate-500 mt-1">Import multiple products from CSV or Excel file</p>
            </div>

            {/* Template Download */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-md mb-6">
                <div className="flex items-start gap-4">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                        <h3 className="text-sm font-bold text-blue-800 mb-2">Download Template First</h3>
                        <p className="text-sm text-blue-700 mb-4">Use our template to ensure your data is formatted correctly.</p>
                        <button
                            onClick={downloadTemplate}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download CSV Template
                        </button>
                    </div>
                </div>
            </div>

            {/* File Upload */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Upload File</h2>

                <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center hover:border-indigo-400 transition-colors">
                    <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                        <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        </div>
                        <p className="text-sm font-medium text-slate-700 mb-1">Click to upload or drag and drop</p>
                        <p className="text-xs text-slate-500">CSV or Excel files only</p>
                    </label>
                    {file && (
                        <div className="mt-4 text-sm text-slate-600">
                            Selected: <span className="font-medium">{file.name}</span>
                        </div>
                    )}
                </div>

                {file && !preview && (
                    <button
                        onClick={handlePreview}
                        className="mt-4 w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all"
                    >
                        Preview Data
                    </button>
                )}
            </div>

            {/* Preview */}
            {preview && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                        <h2 className="text-lg font-bold text-slate-800">Preview (First 10 rows)</h2>
                        <span className="text-sm text-slate-500">{preview.total_rows} rows found</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr>
                                    {preview.headers?.map((header: string, i: number) => (
                                        <th key={i} className="px-4 py-2 text-left text-xs font-bold text-slate-400 uppercase">{String(header)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {preview.rows?.map((row: any, i: number) => (
                                    <tr key={i}>
                                        {preview.headers?.map((header: string, j: number) => (
                                            <td key={j} className="px-4 py-2 text-slate-700">{row[header] || '-'}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                        <button
                            onClick={() => { setPreview(null); setFile(null); }}
                            className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={importing}
                            className={`px-8 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-all ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {importing ? 'Importing...' : 'Import Products'}
                        </button>
                    </div>
                </div>
            )}

            {/* Results */}
            {result && (
                <div className={`rounded-2xl border p-6 ${result.success ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                    <h3 className={`text-lg font-bold mb-4 ${result.success ? 'text-emerald-800' : 'text-rose-800'}`}>
                        Import {result.success ? 'Completed' : 'Failed'}
                    </h3>
                    <div className="space-y-2 text-sm">
                        <p className="text-emerald-700"><strong>Success:</strong> {result.success_count} products imported</p>
                        {result.error_count > 0 && (
                            <p className="text-rose-700"><strong>Errors:</strong> {result.error_count} rows failed</p>
                        )}
                    </div>
                    {result.errors && result.errors.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-bold text-rose-800 mb-2">Error Details:</p>
                            <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
                                {result.errors.map((err: string, i: number) => (
                                    <p key={i} className="text-xs text-rose-600 mb-1">{err}</p>
                                ))}
                            </div>
                        </div>
                    )}
                    <Link
                        to="/products"
                        className="inline-block mt-6 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700"
                    >
                        View Products
                    </Link>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}
        </div>
    );
}

export default BulkImport;
